import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { TestTarget, ItemDetails, ReviewStatus } from "../../types/TestTarget";
import { getStatus, isPendingReview, STATUS_LABELS } from "../../utils/statusUtils";
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  Article as ArticleIcon,
  Dashboard as DashboardIcon,
  Layers as LayersIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  build_component_ref_child_rows,
  build_component_table_display_rows,
  build_view_render_rows,
  is_feature_target,
  resolve_component_ref_target,
  type ComponentTableDisplayRow,
  type ViewItemPanelRow,
} from "../../utils/viewItemTree";
import { as_item_details } from "../../utils/itemParams";

// モーダルオーバーレイ
const ModalOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

// モーダルコンテナ
const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  width: 900px;
  height: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// ヘッダー
const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

// ボディ（スクロール可能なテーブル領域）
const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
`;

// フッター
const ModalFooter = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  font-size: 0.875rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const CreateButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  background: #667eea;
  color: white;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledAddIcon = styled(AddIcon)`
  font-size: 1rem;
`;

// テーブルスタイル
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  table-layout: fixed;
`;

const TableHead = styled.thead`
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.15s ease;

  &:nth-child(even) {
    background: #f8fafc;
  }

  &:hover {
    background: #e2e8f0 !important;
  }
`;

const TableCell = styled.td<{ width?: string }>`
  padding: 4px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.65rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
  width: ${(props) => props.width || "auto"};
  max-width: 0;
  overflow: hidden;
`;

const TableHeaderCell = styled.th<{ width?: string }>`
  padding: 4px;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
  font-weight: 600;
  font-size: 0.65rem;
  color: #475569;
  width: ${(props) => props.width || "auto"};
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
`;

// 階層表示用のコンテナ
const HierarchyContainer = styled.div<{ level: number }>`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: ${(props) => props.level * 16}px;
`;

const ItemIcon = styled.div<{ $isParent: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 6px;
  color: ${(props) => (props.$isParent ? "#667eea" : "#94a3b8")};
  font-size: 14px;
  z-index: 1;
  background: white;
  padding: 2px;
`;

const ItemNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledFolderIcon = styled(FolderIcon)`
  font-size: inherit;
`;

const StyledArticleIcon = styled(ArticleIcon)`
  font-size: inherit;
`;

const StyledDashboardIcon = styled(DashboardIcon)`
  font-size: inherit;
`;

const StyledLayersIcon = styled(LayersIcon)`
  font-size: inherit;
`;

// ステータスバッジ
const StatusBadge = styled.span<{ status: ReviewStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  gap: 4px;
  white-space: nowrap;

  ${(props) => {
    switch (props.status) {
      case "draft":
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
      case "need_review":
        return `
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fbbf24;
        `;
      case "in_review":
        return `
          background: #dbeafe;
          color: #2563eb;
          border: 1px solid #60a5fa;
        `;
      case "approved":
        return `
          background: #d1fae5;
          color: #059669;
          border: 1px solid #34d399;
        `;
      case "rejected":
        return `
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #f87171;
        `;
      case "archived":
        return `
          background: #e5e7eb;
          color: #4b5563;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
    }
  }}
`;

// セレクタなしタグ
const NoSelectorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  white-space: nowrap;
  gap: 4px;
`;

// 手動実行モードタグ
const ManualOperationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #f87171;
  white-space: nowrap;
  gap: 4px;
`;

// 必須/任意インジケーター
const RequiredIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ef4444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: "";
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
    position: absolute;
  }
`;

const OptionalIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: transparent;
  display: inline-flex;
`;

// 選択/解除ボタン
const SelectButton = styled.button<{ $isSelected: boolean }>`
  padding: 4px 12px;
  font-size: 0.7rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  ${(props) =>
    props.$isSelected
      ? `
    background: #ef4444;
    color: white;
    &:hover {
      background: #dc2626;
    }
  `
      : `
    background: #3b82f6;
    color: white;
    &:hover {
      background: #2563eb;
    }
  `}
`;

interface TargetSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (targetId: string) => void;
  selectedTargetId?: string;
  localTestTargets: TestTarget[];
  currentViewId?: string;
  onViewItem?: (item: TestTarget) => void;
  onCreateTestTarget?: (callback?: (newTarget: any) => void) => void;
}

// ヘルパー関数
const hasChildren = (item: TestTarget, allItems: TestTarget[]): boolean => {
  return allItems.some((i) => i.parentId === item.id);
};

// セレクタ存在判定関数
// セレクタが存在しない、または空であるかを判定する
// 複数のセレクタが設定されている場合、すべてのセレクタが有効（空でない）でない限りfalseを返す
const hasSelector = (item: TestTarget): boolean => {
  if (!item.params || typeof item.params !== "object") return false;
  if (!("selector" in item.params)) return false;

  const selector = (item.params as ItemDetails).selector;
  if (!selector) return false;

  if (typeof selector === "string") {
    return selector.trim().length > 0;
  }

  if (typeof selector === "object" && !Array.isArray(selector)) {
    const values = Object.values(selector);
    // すべてのセレクタ値が有効（空でない文字列）である必要がある
    return values.length > 0 && values.every((v) => v && typeof v === "string" && v.trim().length > 0);
  }

  return false;
};

const isManualOperation = (item: TestTarget): boolean => {
  if (!item.params || typeof item.params !== "object") return false;

  const details = item.params as ItemDetails;
  if ("isManual" in details && details.isManual === true) return true;
  if ("is_manual" in details && (details as any).is_manual === true) return true;

  return false;
};

export const TargetSelectionModal: React.FC<TargetSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  selectedTargetId,
  localTestTargets,
  currentViewId,
  onViewItem,
  onCreateTestTarget,
}) => {
  const [expandedRefIds, setExpandedRefIds] = useState<Set<string>>(new Set());

  // 階層レベルを計算する関数
  const calculateLevel = (item: TestTarget, allItems: TestTarget[]): number => {
    let level = 0;
    let currentParentId = item.parentId;
    while (currentParentId) {
      const parentId = currentParentId;
      const parent = allItems.find((t) => t.id === parentId);
      if (!parent) break;
      level++;
      currentParentId = parent.parentId;
    }
    return level;
  };

  const viewTargetForModal = useMemo(() => {
    if (!currentViewId) {
      return null;
    }
    return localTestTargets.find((t) => t.id === currentViewId) ?? null;
  }, [currentViewId, localTestTargets]);

  /** View 直下ツリーの item / section / overlay / component-ref（項目一覧タブと同じ表示単位） */
  const viewRenderRows = useMemo(() => {
    if (!currentViewId) {
      return [];
    }
    return build_view_render_rows(currentViewId, localTestTargets);
  }, [currentViewId, localTestTargets]);

  useEffect(() => {
    setExpandedRefIds(
      new Set(
        viewRenderRows
          .filter((row) => row.type === "component-ref")
          .map((row) => row.id)
      )
    );
  }, [viewRenderRows]);

  /** currentViewId なし時: 従来どおり全ターゲットから item 系のみ */
  const legacyFlatItems = useMemo(() => {
    if (currentViewId) {
      return [];
    }
    return localTestTargets.filter((item) => {
      if (is_feature_target(item)) {
        return false;
      }
      const typeValue = item.type as { name?: string } | string;
      const typeName =
        typeof typeValue === "object" && typeValue?.name
          ? typeValue.name
          : typeValue;
      const itemType = String(typeName).toLowerCase();
      return (
        itemType === "item" ||
        itemType === "section" ||
        itemType === "overlay"
      );
    });
  }, [currentViewId, localTestTargets]);

  /** View.params.component_ids に登録された旧形式の共通コンポーネント配下の項目 */
  const componentTableDisplayRows = useMemo((): ComponentTableDisplayRow[] => {
    if (!viewTargetForModal) {
      return [];
    }
    return build_component_table_display_rows(
      viewTargetForModal,
      localTestTargets
    );
  }, [viewTargetForModal, localTestTargets]);

  const toggleRefExpanded = (refId: string) => {
    setExpandedRefIds((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) {
        next.delete(refId);
      } else {
        next.add(refId);
      }
      return next;
    });
  };

  const handleSelect = (targetId: string) => {
    if (selectedTargetId === targetId) {
      // 既に選択されている場合は解除
      onSelect("");
    } else {
      onSelect(targetId);
    }
    onClose();
  };

  const handleViewItem = (item: TestTarget) => {
    if (onViewItem) {
      onViewItem(item);
    }
  };

  // 遷移先のIDから名前を取得する関数
  const getTransitionTargetName = (targetId: string): string => {
    const target = localTestTargets.find((t: TestTarget) => t.id === targetId);
    return target ? target.name : targetId; // 見つからない場合はIDをそのまま表示
  };

  const renderComponentHeaderRow = (row: {
    rowKind: "component_header";
    rowKey: string;
    name: string;
    depth: number;
  }) => {
    const pad = row.depth * 16;
    return (
      <TableRow key={row.rowKey} style={{ backgroundColor: "#ecfdf5" }}>
        <TableCell width="38%">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: pad,
              gap: 6,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {row.depth > 0 ? (
              <span
                style={{
                  color: "#94a3b8",
                  fontSize: "0.85rem",
                  flexShrink: 0,
                  userSelect: "none",
                }}
                aria-hidden
              >
                ┗
              </span>
            ) : null}
            <ViewModuleIcon
              sx={{ fontSize: 18, color: "#0d9488", flexShrink: 0 }}
            />
            <span style={{ color: "#334155" }}>{row.name}</span>
          </div>
        </TableCell>
        <TableCell width="12%">
          <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
        </TableCell>
        <TableCell width="8%">
          <div
            style={{
              fontSize: "0.6rem",
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            コンポーネント
          </div>
        </TableCell>
        <TableCell width="6%">
          <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
        </TableCell>
        <TableCell width="24%">
          <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
        </TableCell>
        <TableCell width="12%">
          <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
        </TableCell>
      </TableRow>
    );
  };

  const renderItemDataRow = (
    item: TestTarget,
    rowKey: string,
    layout: "view" | "component",
    levelSourceList: TestTarget[],
    panelRow?: ViewItemPanelRow
  ) => {
    const typeValue = item.type as { name?: string } | string;
    const typeName =
      typeof typeValue === "object" && typeValue?.name
        ? typeValue.name
        : typeValue;
    const itemType = String(typeName).toLowerCase();

    // NOTE: selector 無し Item でも transitions 等を表示する
    const details = as_item_details(item.params) ?? ({} as ItemDetails);

    const itemStatus = getStatus(item);
    const hasPendingStatus = isPendingReview(itemStatus);
    const isSelected = selectedTargetId === item.id;

    const chain_depth = panelRow?.componentChainDepth ?? 0;
    const item_level = panelRow?.level ?? 0;
    const component_visual_level =
      layout === "component" ? chain_depth + item_level : item_level;

    return (
      <TableRow
        key={rowKey}
        style={{
          backgroundColor: hasPendingStatus
            ? itemStatus === "draft"
              ? "#fff4e6"
              : itemStatus === "need_review"
                ? "#ffe0b2"
                : "#ffcc80"
            : "transparent",
        }}
      >
        <TableCell width="38%">
          {layout === "view" ? (
            <HierarchyContainer level={calculateLevel(item, levelSourceList)}>
              <ItemIcon $isParent={hasChildren(item, localTestTargets)}>
                {hasChildren(item, localTestTargets) ? (
                  <StyledFolderIcon />
                ) : itemType === "section" ? (
                  <StyledDashboardIcon />
                ) : itemType === "overlay" ? (
                  <StyledLayersIcon />
                ) : (
                  <StyledArticleIcon />
                )}
              </ItemIcon>
              <ItemNameContainer>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "2px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                      color: "#667eea",
                      textDecoration: "none",
                      flex: 1,
                    }}
                    title={`${item.name} - クリックで詳細を表示`}
                    onClick={() => handleViewItem(item)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    {item.name}
                  </div>
                </div>
                {details?.text && (
                  <div
                    style={{
                      fontSize: "0.6rem",
                      color: "#64748b",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={details.text}
                  >
                    {details.text.substring(0, 15)}
                    {details.text.length > 15 && "..."}
                  </div>
                )}
              </ItemNameContainer>
            </HierarchyContainer>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
                paddingLeft: component_visual_level * 16,
                boxSizing: "border-box",
              }}
            >
              {component_visual_level > 0 ? (
                <span
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    marginRight: 4,
                    flexShrink: 0,
                    userSelect: "none",
                  }}
                  aria-hidden
                >
                  ┗
                </span>
              ) : null}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <ItemIcon $isParent={hasChildren(item, localTestTargets)}>
                  {hasChildren(item, localTestTargets) ? (
                    <StyledFolderIcon />
                  ) : itemType === "section" ? (
                    <StyledDashboardIcon />
                  ) : itemType === "overlay" ? (
                    <StyledLayersIcon />
                  ) : (
                    <StyledArticleIcon />
                  )}
                </ItemIcon>
                <ItemNameContainer>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "2px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        color: "#667eea",
                        textDecoration: "none",
                        flex: 1,
                      }}
                      title={`${item.name} - クリックで詳細を表示`}
                      onClick={() => handleViewItem(item)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {item.name}
                    </div>
                  </div>
                  {details?.text && (
                    <div
                      style={{
                        fontSize: "0.6rem",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={details.text}
                    >
                      {details.text.substring(0, 15)}
                      {details.text.length > 15 && "..."}
                    </div>
                  )}
                </ItemNameContainer>
              </div>
            </div>
          )}
        </TableCell>
        <TableCell width="12%">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              alignItems: "flex-start",
            }}
          >
            {hasPendingStatus && (
              <StatusBadge status={itemStatus}>
                {STATUS_LABELS[itemStatus]}
              </StatusBadge>
            )}
            {!hasSelector(item) && (
              <NoSelectorBadge>セレクタなし</NoSelectorBadge>
            )}
            {isManualOperation(item) && (
              <ManualOperationBadge>手動実行モード</ManualOperationBadge>
            )}
          </div>
        </TableCell>
        <TableCell width="8%">
          <div
            style={{
              fontSize: "0.6rem",
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            {itemType === "section"
              ? "セクション"
              : itemType === "overlay"
                ? "オーバーレイ"
                : itemType === "item"
                  ? "アイテム"
                  : typeof typeName === "string"
                    ? typeName
                    : "不明"}
          </div>
        </TableCell>
        <TableCell width="6%">
          <div style={{ display: "flex", justifyContent: "center" }}>
            {details?.htmlAttributes?.required ? (
              <RequiredIndicator title="必須" />
            ) : (
              <OptionalIndicator title="任意" />
            )}
          </div>
        </TableCell>
        <TableCell width="24%">
          {details?.transitions && details.transitions.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {details.transitions.slice(0, 2).map((transition, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "0.6rem",
                    color: "#1e293b",
                  }}
                >
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                    title={`→ ${getTransitionTargetName(
                      transition.targetId || ""
                    )}`}
                  >
                    → {getTransitionTargetName(transition.targetId || "")}
                  </div>
                </div>
              ))}
              {details.transitions.length > 2 && (
                <div
                  style={{
                    fontSize: "0.55rem",
                    color: "#64748b",
                  }}
                >
                  +{details.transitions.length - 2}件
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: "0.6rem", color: "#94a3b8" }}>
              遷移なし
            </div>
          )}
        </TableCell>
        <TableCell width="12%">
          <SelectButton
            $isSelected={isSelected}
            onClick={() => handleSelect(item.id)}
          >
            {isSelected ? "解除" : "選択"}
          </SelectButton>
        </TableCell>
      </TableRow>
    );
  };

  const renderComponentRefRow = (refRow: TestTarget & { level: number }) => {
    const comp = resolve_component_ref_target(refRow, localTestTargets);
    const isDangling = !comp;
    const isExpanded = expandedRefIds.has(refRow.id);
    const childRows = isExpanded
      ? build_component_ref_child_rows(refRow, localTestTargets)
      : [];
    const label = comp ? comp.name : refRow.name;

    return (
      <React.Fragment key={`ref-${refRow.id}`}>
        <TableRow style={{ backgroundColor: "#e8f5e9" }}>
          <TableCell width="38%">
            <HierarchyContainer level={refRow.level}>
              <span
                onClick={() => {
                  if (!isDangling) toggleRefExpanded(refRow.id);
                }}
                style={{
                  cursor: isDangling ? "default" : "pointer",
                  marginRight: 4,
                  userSelect: "none",
                  color: "#0f766e",
                }}
                aria-label={isExpanded ? "折りたたむ" : "展開する"}
              >
                {isDangling ? "•" : isExpanded ? "▼" : "▶"}
              </span>
              <span style={{ marginRight: 4 }}>🔗</span>
              <ItemNameContainer>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#0f766e",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={label}
                  >
                    {label}
                  </span>
                  <span style={{ fontSize: "0.6rem", color: "#6b7280", flexShrink: 0 }}>
                    （共通コンポーネント）
                  </span>
                </div>
                {isDangling && (
                  <span style={{ fontSize: "0.6rem", color: "#dc2626" }}>
                    参照先が見つかりません
                  </span>
                )}
              </ItemNameContainer>
            </HierarchyContainer>
          </TableCell>
          <TableCell width="12%">
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width="8%">
            <div
              style={{
                fontSize: "0.6rem",
                color: "#0f766e",
                textAlign: "center",
              }}
            >
              コンポーネント参照
            </div>
          </TableCell>
          <TableCell width="6%">
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width="24%">
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width="12%">
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
        </TableRow>
        {childRows.map((child) =>
          renderItemDataRow(
            child,
            `ref-${refRow.id}-child-${child.id}`,
            "component",
            viewRenderRows,
            { ...child, componentChainDepth: refRow.level + 1 }
          )
        )}
      </React.Fragment>
    );
  };

  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        <TableHeaderCell width="38%">項目名</TableHeaderCell>
        <TableHeaderCell width="12%">ラベル</TableHeaderCell>
        <TableHeaderCell width="8%">タイプ</TableHeaderCell>
        <TableHeaderCell width="6%">必須</TableHeaderCell>
        <TableHeaderCell width="24%">画面遷移</TableHeaderCell>
        <TableHeaderCell width="12%">操作</TableHeaderCell>
      </TableRow>
    </TableHead>
  );

  if (!open) return null;

  return createPortal(
    <ModalOverlay open={open} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>ターゲットを選択</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {!currentViewId ? (
            <TableWrapper>
              <Table>
                {renderTableHead()}
                <TableBody>
                  {legacyFlatItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        style={{ textAlign: "center", padding: "32px" }}
                      >
                        表示するアイテムがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    legacyFlatItems.map((item) =>
                      renderItemDataRow(
                        item,
                        item.id,
                        "view",
                        legacyFlatItems
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
          ) : (
            <>
              {componentTableDisplayRows.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <ViewModuleIcon sx={{ fontSize: 18, color: "#0d9488" }} />
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#134e4a",
                        fontSize: "0.8rem",
                      }}
                    >
                      共通コンポーネントの項目
                    </span>
                  </div>
                  <TableWrapper>
                    <Table>
                      {renderTableHead()}
                      <TableBody>
                        {componentTableDisplayRows.map((row) =>
                          row.rowKind === "component_header"
                            ? renderComponentHeaderRow(row)
                            : renderItemDataRow(
                                row.panelRow,
                                row.rowKey,
                                "component",
                                viewRenderRows,
                                row.panelRow
                              )
                        )}
                      </TableBody>
                    </Table>
                  </TableWrapper>
                </div>
              )}
              {viewRenderRows.length > 0 && (
                <div>
                  {componentTableDisplayRows.length > 0 && (
                    <div
                      style={{
                        marginBottom: 8,
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8rem",
                      }}
                    >
                      ビュー直下の項目
                    </div>
                  )}
                  <TableWrapper>
                    <Table>
                      {renderTableHead()}
                      <TableBody>
                        {viewRenderRows.map((item) =>
                          item.type === "component-ref"
                            ? renderComponentRefRow(item)
                            : renderItemDataRow(
                                item,
                                `view-${item.id}`,
                                "view",
                                viewRenderRows
                              )
                        )}
                      </TableBody>
                    </Table>
                  </TableWrapper>
                </div>
              )}
              {componentTableDisplayRows.length === 0 &&
                viewRenderRows.length === 0 && (
                  <TableWrapper>
                    <Table>
                      {renderTableHead()}
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            style={{ textAlign: "center", padding: "32px" }}
                          >
                            表示するアイテムがありません
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableWrapper>
                )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <div>
            {onCreateTestTarget && (
              <CreateButton
                onClick={() => {
                  onCreateTestTarget((newTarget: any) => {
                    if (newTarget && newTarget.id) {
                      onSelect(newTarget.id);
                      onClose();
                    }
                  });
                }}
              >
                <StyledAddIcon /> 項目を追加
              </CreateButton>
            )}
          </div>
          <CancelButton onClick={onClose}>キャンセル</CancelButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>,
    document.body
  );
};
